/**
 * SSE parsing for the platform's streaming chat endpoints.
 *
 * `POST /api/chat` (model playground) and `POST /api/review/chat` (ask about
 * a reviewed run) emit the same envelope — `data: {json}\n\n` carrying the
 * event union below — because both wrap `chat_proxy.stream_chat_completion`
 * on the server. Parsing it lives here so the two consumers cannot drift.
 * A training agent session's stream (TRA-1327) uses the same framing for
 * its own events, so the frame readers hand over plain JSON for it to check.
 *
 * Deliberately not the AI SDK: `@ai-sdk/react` is in package.json but unused,
 * and its data-stream protocol is not what these endpoints speak.
 */

import { notifyIfSessionExpired } from '@/lib/sessionExpiry'

export interface SSETextDelta {
  type: 'text_delta'
  content: string
}

export interface SSEThinkingDelta {
  type: 'thinking_delta'
  content: string
}

export interface SSEToolCallStart {
  type: 'tool_call_start'
  id: string
  name: string
  args: string
}

export interface SSEToolCallDelta {
  type: 'tool_call_delta'
  id: string
  args_delta: string
}

export interface SSEDone {
  type: 'done'
}

export interface SSEError {
  type: 'error'
  content: string
}

export type SSEEvent =
  | SSETextDelta
  | SSEThinkingDelta
  | SSEToolCallStart
  | SSEToolCallDelta
  | SSEDone
  | SSEError

/**
 * The JSON of each frame in one chunk of an SSE body, plus whatever is left
 * over.
 *
 * The trailing partial line is returned rather than parsed: a chunk boundary
 * falls mid-JSON often enough that dropping the remainder loses tokens.
 * Unparseable frames are skipped loudly instead of aborting the stream.
 */
export function parseSseFrames(
  buffer: string,
  onParseError?: (raw: string) => void,
): { frames: unknown[]; rest: string } {
  const lines = buffer.split('\n')
  const rest = lines.pop() ?? ''
  const frames: unknown[] = []

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed.startsWith('data: ')) continue
    const jsonStr = trimmed.slice(6)
    try {
      frames.push(JSON.parse(jsonStr))
    } catch {
      onParseError?.(jsonStr)
    }
  }

  return { frames, rest }
}

/** Read an SSE response body to completion, handing over each frame's JSON. */
export async function readSseFrames(
  body: ReadableStream<Uint8Array>,
  onFrame: (frame: unknown) => void,
  onParseError?: (raw: string) => void,
): Promise<void> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const { frames, rest } = parseSseFrames(buffer, onParseError)
    buffer = rest
    for (const frame of frames) {
      onFrame(frame)
    }
  }
}

/** Read a chat endpoint's SSE body to completion, handing over decoded events. */
export async function readSseStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: SSEEvent) => void,
  onParseError?: (raw: string) => void,
): Promise<void> {
  await readSseFrames(
    body,
    (frame) => {
      onEvent(frame as SSEEvent)
    },
    onParseError,
  )
}

/**
 * The `detail` of a failed JSON response, or a status-code fallback.
 *
 * Both streaming hooks route every non-OK response through here, which is
 * why the dead-session report lives here rather than at each `fetch`.
 */
export async function readErrorDetail(response: Response): Promise<string> {
  notifyIfSessionExpired(response.status)
  // `detail` is a string for our own HTTPExceptions but a list of error
  // objects for FastAPI's request validation — typing it as a string let the
  // array through, and `new Error(...)` rendered it to the user as
  // "[object Object]". Anything that is not text falls back to the status.
  const data = (await response.json().catch(() => ({}))) as { detail?: unknown }
  return typeof data.detail === 'string'
    ? data.detail
    : `API error: ${response.status.toString()}`
}
